import { createSlice, PayloadAction } from '@reduxjs/toolkit';

/**
 * Modal State Management
 *
 * Centralizes control of all modal visibility states.
 * Replaces Bootstrap data-bs-toggle/target attributes with React state.
 */

export type ChartModalTab = 'tradingview' | 'chart';

export type DeepSubTab = 'analysis' | 'chart';

/** Mobile-only orthogonal section within Deep Analysis (sub-page navigation).
 *  Desktop ignores this entirely and continues using DeepSubTab. */
export type MobileDeepSection =
  | 'search'
  | 'overview'
  | 'live-chart'
  | 'analyze-chart'
  | 'analyze-details';

/** Why the upgrade prompt was opened - drives context-aware copy/CTA. */
export type UpgradeReason =
  | 'ai_analysis'
  | 'scan_limit'
  | 'universe'
  | 'favorites'
  | 'export'
  | 'auto_screen';

interface ModalsState {
  filtersModal: boolean;
  finvizScreenerModal: boolean;
  legacyFiltersModal: boolean;
  chartModal: boolean;
  /** Which tab to activate when ChartModal opens (null = keep default). Cleared after read. */
  chartModalInitialTab: ChartModalTab | null;
  /** When set, App switches main nav to this tab (e.g. 'charts' or 'deep_analysis') then clears.
   *  For switching to a Deep Analysis sub-tab, also dispatch requestDeepSubTab(...) before this. */
  pendingMainTab: string | null;
  /** When set, the Deep Analysis tab activates this sub-tab on mount/next render then clears. */
  pendingDeepSubTab: DeepSubTab | null;
  /** Mobile-only section. Persisted across reloads; desktop ignores. */
  mobileDeepSection: MobileDeepSection;
  /** When set, the Deep Analysis tab activates this mobile section on next render then clears. */
  pendingMobileDeepSection: MobileDeepSection | null;
  /** Origin tab for Back button in Deep Analysis. */
  deepAnalysisReturnTab: 'screening' | 'charts' | null;
  aiAnalysisModal: boolean;
  grokSentimentModal: boolean;
  perplexityModal: boolean;
  stockActionModal: boolean;
  loginModal: boolean;
  registerModal: boolean;
  upgradePrompt: boolean;
  /** Why the upgrade prompt is showing (null = generic). Non-boolean; excluded from ModalBooleanKey. */
  upgradePromptReason: UpgradeReason | null;
  aboutModal: boolean;
  contactSupportModal: boolean;
  feedbackModal: boolean;
  termsModal: boolean;
  privacyModal: boolean;
  disclaimerModal: boolean;
}

type ModalBooleanKey = Exclude<
  keyof ModalsState,
  | 'pendingMainTab'
  | 'chartModalInitialTab'
  | 'deepAnalysisReturnTab'
  | 'pendingDeepSubTab'
  | 'mobileDeepSection'
  | 'pendingMobileDeepSection'
  | 'upgradePromptReason'
>;

const initialState: ModalsState = {
  filtersModal: false,
  finvizScreenerModal: false,
  legacyFiltersModal: false,
  chartModal: false,
  chartModalInitialTab: null,
  pendingMainTab: null,
  pendingDeepSubTab: null,
  mobileDeepSection: 'overview',
  pendingMobileDeepSection: null,
  deepAnalysisReturnTab: null,
  aiAnalysisModal: false,
  grokSentimentModal: false,
  perplexityModal: false,
  stockActionModal: false,
  loginModal: false,
  registerModal: false,
  upgradePrompt: false,
  upgradePromptReason: null,
  aboutModal: false,
  contactSupportModal: false,
  feedbackModal: false,
  termsModal: false,
  privacyModal: false,
  disclaimerModal: false,
};

export const modalsSlice = createSlice({
  name: 'modals',
  initialState,
  reducers: {
    openFiltersModal: (state) => {
      state.filtersModal = true;
      state.legacyFiltersModal = false;
      state.finvizScreenerModal = false;
    },
    openFinvizScreenerModal: (state) => {
      state.finvizScreenerModal = true;
      state.filtersModal = false;
      state.legacyFiltersModal = false;
    },
    closeFinvizScreenerModal: (state) => {
      state.finvizScreenerModal = false;
    },
    closeFiltersModal: (state) => {
      state.filtersModal = false;
    },
    toggleFiltersModal: (state) => {
      state.filtersModal = !state.filtersModal;
    },
    openLegacyFiltersModal: (state) => {
      state.legacyFiltersModal = true;
      state.filtersModal = false;
      state.finvizScreenerModal = false;
    },
    closeLegacyFiltersModal: (state) => {
      state.legacyFiltersModal = false;
    },
    openChartModal: (state, action: PayloadAction<ChartModalTab | undefined>) => {
      state.chartModal = true;
      state.chartModalInitialTab = action.payload ?? null;
    },
    closeChartModal: (state) => {
      state.chartModal = false;
      state.chartModalInitialTab = null;
    },
    clearChartModalInitialTab: (state) => {
      state.chartModalInitialTab = null;
    },
    requestNavigateMainTab: (state, action: PayloadAction<string>) => {
      state.pendingMainTab = action.payload;
    },
    clearPendingMainTab: (state) => {
      state.pendingMainTab = null;
    },
    requestDeepSubTab: (state, action: PayloadAction<DeepSubTab>) => {
      state.pendingDeepSubTab = action.payload;
    },
    clearPendingDeepSubTab: (state) => {
      state.pendingDeepSubTab = null;
    },
    setMobileDeepSection: (state, action: PayloadAction<MobileDeepSection>) => {
      state.mobileDeepSection = action.payload;
    },
    requestMobileDeepSection: (state, action: PayloadAction<MobileDeepSection>) => {
      state.pendingMobileDeepSection = action.payload;
    },
    clearPendingMobileDeepSection: (state) => {
      state.pendingMobileDeepSection = null;
    },
    setDeepAnalysisReturnTab: (
      state,
      action: PayloadAction<'screening' | 'charts' | null>
    ) => {
      state.deepAnalysisReturnTab = action.payload;
    },
    clearDeepAnalysisReturnTab: (state) => {
      state.deepAnalysisReturnTab = null;
    },
    openAIAnalysisModal: (state) => {
      state.aiAnalysisModal = true;
    },
    closeAIAnalysisModal: (state) => {
      state.aiAnalysisModal = false;
    },
    openGrokSentimentModal: (state) => {
      state.grokSentimentModal = true;
    },
    closeGrokSentimentModal: (state) => {
      state.grokSentimentModal = false;
    },
    openPerplexityModal: (state) => {
      state.perplexityModal = true;
    },
    closePerplexityModal: (state) => {
      state.perplexityModal = false;
    },
    openStockActionModal: (state) => {
      state.stockActionModal = true;
    },
    closeStockActionModal: (state) => {
      state.stockActionModal = false;
    },
    openLoginModal: (state) => {
      state.loginModal = true;
    },
    closeLoginModal: (state) => {
      state.loginModal = false;
    },
    openRegisterModal: (state) => {
      state.registerModal = true;
    },
    closeRegisterModal: (state) => {
      state.registerModal = false;
    },
    closeAllModals: (state) => {
      state.filtersModal = false;
      state.finvizScreenerModal = false;
      state.legacyFiltersModal = false;
      state.chartModal = false;
      state.chartModalInitialTab = null;
      state.aiAnalysisModal = false;
      state.grokSentimentModal = false;
      state.perplexityModal = false;
      state.stockActionModal = false;
      state.loginModal = false;
      state.registerModal = false;
      state.upgradePrompt = false;
      state.upgradePromptReason = null;
      state.aboutModal = false;
      state.contactSupportModal = false;
      state.feedbackModal = false;
      state.termsModal = false;
      state.privacyModal = false;
      state.disclaimerModal = false;
      state.pendingMainTab = null;
      state.pendingDeepSubTab = null;
      state.pendingMobileDeepSection = null;
      state.deepAnalysisReturnTab = null;
    },
    openUpgradePrompt: (state, action: PayloadAction<UpgradeReason | undefined>) => {
      state.upgradePrompt = true;
      state.upgradePromptReason = action.payload ?? null;
    },
    closeUpgradePrompt: (state) => {
      state.upgradePrompt = false;
      state.upgradePromptReason = null;
    },
    openAboutModal: (state) => {
      state.aboutModal = true;
    },
    closeAboutModal: (state) => {
      state.aboutModal = false;
    },
    openContactSupportModal: (state) => {
      state.contactSupportModal = true;
    },
    closeContactSupportModal: (state) => {
      state.contactSupportModal = false;
    },
    openFeedbackModal: (state) => {
      state.feedbackModal = true;
    },
    closeFeedbackModal: (state) => {
      state.feedbackModal = false;
    },
    openTermsModal: (state) => {
      state.termsModal = true;
    },
    closeTermsModal: (state) => {
      state.termsModal = false;
    },
    openPrivacyModal: (state) => {
      state.privacyModal = true;
    },
    closePrivacyModal: (state) => {
      state.privacyModal = false;
    },
    openDisclaimerModal: (state) => {
      state.disclaimerModal = true;
    },
    closeDisclaimerModal: (state) => {
      state.disclaimerModal = false;
    },
    setModalOpen: (state, action: PayloadAction<{ modal: ModalBooleanKey; open: boolean }>) => {
      const { modal, open } = action.payload;
      state[modal] = open;
    },
  },
});

export const {
  openFiltersModal,
  closeFiltersModal,
  toggleFiltersModal,
  openFinvizScreenerModal,
  closeFinvizScreenerModal,
  openLegacyFiltersModal,
  closeLegacyFiltersModal,
  openChartModal,
  closeChartModal,
  clearChartModalInitialTab,
  requestNavigateMainTab,
  clearPendingMainTab,
  requestDeepSubTab,
  clearPendingDeepSubTab,
  setMobileDeepSection,
  requestMobileDeepSection,
  clearPendingMobileDeepSection,
  setDeepAnalysisReturnTab,
  clearDeepAnalysisReturnTab,
  openAIAnalysisModal,
  closeAIAnalysisModal,
  openGrokSentimentModal,
  closeGrokSentimentModal,
  openPerplexityModal,
  closePerplexityModal,
  openStockActionModal,
  closeStockActionModal,
  openLoginModal,
  closeLoginModal,
  openRegisterModal,
  closeRegisterModal,
  closeAllModals,
  setModalOpen,
  openUpgradePrompt,
  closeUpgradePrompt,
  openAboutModal,
  closeAboutModal,
  openContactSupportModal,
  closeContactSupportModal,
  openFeedbackModal,
  closeFeedbackModal,
  openTermsModal,
  closeTermsModal,
  openPrivacyModal,
  closePrivacyModal,
  openDisclaimerModal,
  closeDisclaimerModal,
} = modalsSlice.actions;

export default modalsSlice.reducer;
